import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ChevronDown, BookOpen } from "lucide-react";

interface Course {
  id: string;
  slug: string;
  title: string;
}

interface CourseSwitcherProps {
  currentCourseTitle: string;
}

export function CourseSwitcherDropdown({ currentCourseTitle }: CourseSwitcherProps) {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("courses");
        if (error || !data || !Array.isArray(data)) return;

        const courseList: Course[] = [...data];
        const requiredSlugs = ["astronomy", "astrophysics", "quantum-mechanics"];
        const missingSlugs = requiredSlugs.filter((slug) => !courseList.some((course) => course.slug === slug));

        if (missingSlugs.length > 0) {
          const { data: fetchedCourses, error: fetchError } = await supabase.functions.invoke("courses", {
            body: { slugs: missingSlugs }
          });

          if (!fetchError && Array.isArray(fetchedCourses)) {
            fetchedCourses.forEach((course: Course) => {
              courseList.push(course);
            });
          }
        }

        // Deduplicate
        const uniqueBySlug = new Map<string, Course>();
        courseList.forEach((course) => {
          uniqueBySlug.set(course.slug, course);
        });

        const allCourses = Array.from(uniqueBySlug.values());
        setCourses(allCourses);
      } catch (err) {
        console.error("Failed to load courses for switcher", err);
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, []);

  const handleSelectCourse = async (course: Course) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;

    await supabase
      .from('profiles')
      .update({ current_course: course.id })
      .eq('id', session.user.id);

    // Navigate to /home and reload the page so the tree re-renders
    window.location.href = '/home';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 h-auto py-1 px-2 -ml-2 text-lg font-bold hover:bg-muted/50 truncate max-w-[200px] sm:max-w-[300px]">
          <span className="truncate">{currentCourseTitle}</span>
          <ChevronDown className="w-4 h-4 opacity-50 flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[240px]">
        {loading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">Loading subjects...</div>
        ) : courses.length > 0 ? (
          courses.map((course) => (
            <DropdownMenuItem
              key={course.id}
              onClick={() => handleSelectCourse(course)}
              className="flex items-center gap-2 cursor-pointer py-2"
            >
              <BookOpen className="w-4 h-4 opacity-70" />
              <span className="truncate flex-1">{course.title}</span>
            </DropdownMenuItem>
          ))
        ) : (
          <div className="p-4 text-center text-sm text-muted-foreground">No other subjects found</div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
